// ClientOrdersPage.jsx
import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  MenuItem
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { heIL } from '@mui/x-data-grid/locales';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ResponsiveToolbar from '../components/ResponsiveToolbar';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { startOfMonth, endOfMonth } from 'date-fns';

const theme = createTheme({
  direction: 'rtl',
  ...heIL,
});

// Custom toolbar component
function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
    </GridToolbarContainer>
  );
}

const ClientOrdersPage = () => {
  const { business_id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const role = userProfile?.role;

  // State
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // UI config
  const uiConfig = userProfile?.ui_config || {};
  const orderFieldsSchema = uiConfig.order_fields_schema || {};

  // Fetch client details
  const {
    data: client,
    isLoading: clientLoading,
    isError: clientError,
  } = useQuery({
    queryKey: ['client', business_id],
    queryFn: async () => {
      const response = await API.get(`/client/?business_id=${business_id}`);
      return response.data;
    },
    enabled: !!business_id,
  });

  // Fetch client orders
  const {
    data: orders = [],
    isLoading: ordersLoading,
    isError: ordersError,
    error: ordersErrorDetails,
  } = useQuery({
    queryKey: ['clientOrders', business_id],
    queryFn: async () => {
      const response = await API.get(`/client/orders/?business_id=${business_id}`);
      return response.data;
    },
    enabled: !!business_id,
    staleTime: 5 * 60 * 1000,
  });

  // Filter orders based on status and date range
  const filteredOrders = useMemo(() => {
    let filtered = orders;
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(order => order.Status === statusFilter);
    }
    
    // Apply date filters
    if (startDate instanceof Date && !isNaN(startDate)) {
      const startTime = startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.OrderDate).getTime();
        return orderDate >= startTime;
      });
    }
    
    if (endDate instanceof Date && !isNaN(endDate)) {
      const endTime = endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.OrderDate).getTime();
        return orderDate <= endTime;
      });
    }
    
    return filtered;
  }, [orders, statusFilter, startDate, endDate]);

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#fff3e0', color: '#ff9800' };
      case 'processing':
        return { bg: '#e1f5fe', color: '#0288d1' };
      case 'completed':
        return { bg: '#e8f5e9', color: '#4caf50' };
      case 'cancelled':
        return { bg: '#ffebee', color: '#f44336' };
      default:
        return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  // Get status display name
  const getStatusName = (status) => {
    const statusOptions = orderFieldsSchema.Status?.options || [];
    const option = statusOptions.find(opt => opt.value === status);
    return option?.label || status;
  };

  // Define columns for DataGrid
  const columns = [
    {
      field: 'OrderID',
      headerName: orderFieldsSchema.OrderID?.label || 'מספר הזמנה',
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="text"
          color="primary"
          onClick={() => navigate(`/order/${params.value}`, { state: { from: `/client/${business_id}` } })}
          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: 'OrderDate',
      headerName: orderFieldsSchema.OrderDate?.label || 'תאריך הזמנה',
      flex: 1.5,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('he-IL');
      },
    },
    {
      field: 'Status',
      headerName: orderFieldsSchema.Status?.label || 'סטטוס',
      flex: 1,
      renderCell: (params) => {
        const statusColors = getStatusColor(params.value);
        return (
          <Chip
            label={getStatusName(params.value)}
            sx={{
              backgroundColor: statusColors.bg,
              color: statusColors.color,
              borderColor: statusColors.color,
              fontWeight: 'medium',
            }}
            variant="outlined"
            size="small"
          />
        );
      },
    },
    {
      field: 'TotalAmount',
      headerName: orderFieldsSchema.TotalAmount?.label || 'סכום כולל',
      flex: 1,
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '';
        return new Intl.NumberFormat('he-IL', {
          style: 'currency',
          currency: 'ILS',
        }).format(params.value);
      },
    },
    {
      field: 'PaidAmount',
      headerName: orderFieldsSchema.PaidAmount?.label || 'סכום ששולם',
      flex: 1,
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '';
        return new Intl.NumberFormat('he-IL', {
          style: 'currency',
          currency: 'ILS',
        }).format(params.value);
      },
    },
    {
      field: 'PaymentStatus',
      headerName: 'מצב תשלום',
      flex: 1,
      valueGetter: (params) => {
        const total = parseFloat(params.row.TotalAmount) || 0;
        const paid = parseFloat(params.row.PaidAmount) || 0;
        if (paid >= total) return 'fully_paid';
        if (paid > 0) return 'partial_paid';
        return 'unpaid';
      },
      renderCell: (params) => {
        let label = 'לא שולם';
        let color = { bg: '#ffebee', color: '#f44336' };
        
        if (params.value === 'fully_paid') {
          label = 'שולם במלואו';
          color = { bg: '#e8f5e9', color: '#4caf50' };
        } else if (params.value === 'partial_paid') {
          label = 'שולם חלקית';
          color = { bg: '#e1f5fe', color: '#0288d1' };
        }
        
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: color.bg,
              color: color.color,
              borderColor: color.color,
              fontWeight: 'medium',
            }}
            variant="outlined"
            size="small"
          />
        );
      },
    },
  ];

  // Financial summary
  const financialSummary = {
    totalOrders: orders.length,
    totalAmount: orders.reduce((sum, order) => sum + (parseFloat(order.TotalAmount) || 0), 0),
    paidAmount: orders.reduce((sum, order) => sum + (parseFloat(order.PaidAmount) || 0), 0),
    pendingAmount: orders.reduce((sum, order) => {
      const total = parseFloat(order.TotalAmount) || 0;
      const paid = parseFloat(order.PaidAmount) || 0;
      return sum + Math.max(0, total - paid);
    }, 0),
  };

  const isLoading = clientLoading || ordersLoading;
  const isError = clientError || ordersError;

  if (isLoading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          {ordersErrorDetails?.response?.data?.error || 'שגיאה בטעינת הזמנות'}
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <ThemeProvider theme={theme}>
        <div dir="rtl">
          <Container maxWidth="lg" sx={{ my: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4">
                הזמנות: {client?.ClientName}
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(`/client/${business_id}`)}
                  sx={{ mr: 1 }}
                >
                  חזרה לפרטי לקוח
                </Button>
                {role === 'manager' && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/order/new?client=${business_id}`)}
                  >
                    הזמנה חדשה
                  </Button>
                )}
              </Box>
            </Box>

            {/* Financial Summary Card */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  סיכום הזמנות
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      מספר הזמנות
                    </Typography>
                    <Typography variant="h6">
                      {financialSummary.totalOrders}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      סכום כולל
                    </Typography>
                    <Typography variant="h6">
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency',
                        currency: 'ILS',
                      }).format(financialSummary.totalAmount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      סכום ששולם
                    </Typography>
                    <Typography variant="h6">
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency',
                        currency: 'ILS',
                      }).format(financialSummary.paidAmount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      יתרה לתשלום
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency',
                        currency: 'ILS',
                      }).format(financialSummary.pendingAmount)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Orders DataGrid */}
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardHeader
                title={
                  <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                    כל ההזמנות
                  </Typography>
                }
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  py: 1.5,
                }}
              />
              <CardContent>
                {/* Filters row */}
                <ResponsiveToolbar>
                  <TextField
                    select
                    label="סטטוס"
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ minWidth: { xs: 0, md: 150 } }}
                  >
                    <MenuItem value="">הכל</MenuItem>
                    {orderFieldsSchema.Status?.options?.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <DatePicker
                    label="חודש"
                    value={startDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setStartDate(startOfMonth(newValue));
                        setEndDate(endOfMonth(newValue));
                      }
                    }}
                    views={['month', 'year']}
                    slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } } } }}
                  />

                  <DatePicker
                    label="מתאריך"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } } } }}
                  />

                  <DatePicker
                    label="עד תאריך"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } } } }}
                  />
                </ResponsiveToolbar>

                {filteredOrders.length === 0 ? (
                  <Alert severity="info" sx={{ my: 2 }}>
                    אין הזמנות התואמות את החיפוש
                  </Alert>
                ) : (
                  <Box sx={{ height: 500, width: '100%' }}>
                    <DataGrid
                      rows={filteredOrders}
                      columns={columns}
                      getRowId={(row) => row.OrderID}
                      pageSize={10}
                      rowsPerPageOptions={[10, 25, 50]}
                      disableSelectionOnClick
                      localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                      components={{
                        Toolbar: CustomToolbar,
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Container>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default ClientOrdersPage;